import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { CommonModule, NgClass, NgIf } from "@angular/common";
import { MenuService } from "../../services/menu.service";
import { SubMenuItem, MenuItem } from "../../models/menu.model";
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterLinkActive,
} from "@angular/router";
import { AuthService } from "../../services/auth/auth.service";
import { SidebarSubmenuComponent } from "./sidebar-submenu/sidebar-submenu.component";
import { User } from "../../../admin/models/users.model";
import { FilterApiModel } from "../../../company/models/FilterApi.model";
import * as feather from "feather-icons";
import { Statistics } from "../../../admin/models/statistics.model";
import { StatisticService } from "../../../admin/services/statistic.service";
import { ThemeService } from "../../services/theme.service";
import { EcommerceService } from "../../../admin/services/ecommerce.service";
import { catchError } from "rxjs/operators";
import { of } from "rxjs";

interface DashboardMetricNotif {
  value: number;
}

@Component({
  selector: "app-sidebar",
  standalone: true,
  imports: [
    NgClass,
    NgIf,
    RouterLink,
    CommonModule,
    RouterLinkActive,
    SidebarSubmenuComponent,
  ],
  templateUrl: "./sidebar.component.html",
  styleUrl: "./sidebar.component.css",
})
export class SidebarComponent implements OnInit, AfterViewInit {
  userAuth = signal<any>([]);
  user = signal<User | null>(null);
  errorMessage: string = "";
  today: Date = new Date();
  formattedToday: string = this.today.toISOString().split("T")[0];
  startDate: string = "2024-10-01";
  endDate: string = this.formattedToday;

  searchQuery = signal<string>("");
  filteredMenu = signal<MenuItem[]>([]);

  @ViewChild("searchInput", { static: false })
  searchInput!: ElementRef<HTMLInputElement>;

  notifMetric: DashboardMetricNotif[] = [
    {
      value: 0,
    },
    {
      value: 0,
    },
    {
      value: 0,
    },
    {
      value: 0,
    },
    {
      value: 0,
    },
    {
      value: 0,
    },
  ];
  pendingEcommerceOrdersCount = 0;

  constructor(
    public menuService: MenuService,
    private statisticService: StatisticService,
    protected authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    public themeService: ThemeService,
    private ecommerceService: EcommerceService
  ) { }

  ngOnInit(): void {
    const jwtToken = localStorage.getItem("jwtToken");
    if (jwtToken) {
      this.user.set(this.route.snapshot.data["user"].data);
      if (this.user()) {
        this.userAuth.set(this.user());
      }
    }
    if (this.user()) {
      this.fetchDashboardData(this.startDate, this.endDate);
      // Only fetch pending ecommerce orders for BACKOFFICE_ADMIN
      if (this.user()?.plateform === "BACKOFFICE_ADMIN") {
        this.fetchPendingEcommerceOrders();
      }
    }

    // Initialiser le menu filtré
    this.filteredMenu.set(this.menuService.pagesMenu || []);
  }

  statistics: Statistics = new Statistics();
  fetchDashboardData(startDate: string, endDate: string): void {
    if (this.user()?.plateform === "BACKOFFICE_ADMIN") {
      this.statisticService.dashboard(startDate, endDate).subscribe((data) => {
        this.statistics = data?.data || new Statistics();
        this.mapper();
      });
    } else {
      this.statisticService
        .dashboardEnterprise(startDate, endDate)
        .subscribe((data) => {
          this.statistics = data?.data || new Statistics();

          this.mapper();
        });
    }
  }

  private mapper(): void {
    this.notifMetric[0].value = this.statistics.employeeWaitingValidation;
    this.notifMetric[1].value = this.statistics.depositMerchantInitiated;
    this.notifMetric[2].value = this.statistics.pendingIncreasePaymentCount;
    this.notifMetric[3].value = this.statistics.pendingFundRequestCount;
    this.notifMetric[4].value = this.statistics.internalTransferInitiatedCount;
    this.notifMetric[5].value =
      (this.statistics.pendingMerchantRateChangeCount || 0) +
      (this.statistics.pendingMerchantSettlementRequestCount || 0);
  }

  private fetchPendingEcommerceOrders(): void {
    const paidFilter = new FilterApiModel(1, 1, "status!='PENDING';status!='AWAITING_PAYMENT';status!='PAYMENT_PENDING';status!='PAYMENT_PROCESSING';status!='PAYMENT_FAILED';status!='COMPLETED';status!='CANCELLED';status!='REFUNDED'");
    this.ecommerceService.getOrders(paidFilter).pipe(
      catchError(() => of(null))
    ).subscribe((response: any) => {
      if (response?.meta?.totalItems != null) {
        this.pendingEcommerceOrdersCount = response.meta.totalItems;
      } else if (response?.data) {
        this.pendingEcommerceOrdersCount = response.data.length;
      }
    });
  }

  ngAfterViewInit() {
    feather.replace();
  }

  protected readonly FilterApiModel = FilterApiModel;
  filterRsql: FilterApiModel = new FilterApiModel(
    1,
    10,
    "kyc=='KYC_REQUEST_2',signatureContractStatus=='SIGNED_REQUEST'"
  );

  public toggleMenu(subMenu: SubMenuItem) {
    console.log('Toggle menu clicked:', subMenu.label, subMenu.expanded);
    this.menuService.toggleMenu(subMenu);
  }

  public logout() {
    this.authService.logout();
    this.router.navigate(["/login"]);
  }

  public closeMobileMenu() {
    this.menuService.showMobileMenu = false;
  }

  public onMenuItemClick() {
    if (this.menuService.showMobileMenu) {
      this.closeMobileMenu();
    }
  }

  public toggleTheme() {
    this.themeService.toggleTheme();
  }

  public onSearchChange(query: string) {
    this.searchQuery.set(query);
    this.filterMenu();
  }

  private filterMenu() {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) {
      this.filteredMenu.set(this.menuService.pagesMenu || []);
      return;
    }

    const filtered = (this.menuService.pagesMenu || [])
      .map((menuSection) => ({
        ...menuSection,
        items: menuSection.items.filter(
          (item) =>
            (item.label && item.label.toLowerCase().includes(query)) ||
            (item.children &&
              item.children.some(
                (child) =>
                  child.label && child.label.toLowerCase().includes(query)
              ))
        ),
      }))
      .filter((menuSection) => menuSection.items.length > 0);

    this.filteredMenu.set(filtered);
  }

  public clearSearch() {
    this.searchQuery.set("");
    this.filterMenu();
  }

  @HostListener("document:keydown", ["$event"])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Raccourci Ctrl+K ou Cmd+K pour la recherche
    if ((event.ctrlKey || event.metaKey) && event.key === "k") {
      event.preventDefault();
      if (this.menuService.showSideBar && this.searchInput) {
        this.searchInput.nativeElement.focus();
      }
    }

    // Échap pour effacer la recherche
    if (event.key === "Escape" && this.searchQuery()) {
      this.clearSearch();
    }
  }

  // Fonction pour vérifier les permissions d'un item
  hasItemPermission(item: any): boolean {
    // Si l'item n'a pas de permission spécifique, on l'affiche
    if (
      !item.routePermission &&
      !item.childPermission1 &&
      !item.childPermission2 &&
      !item.childPermission3 &&
      !item.childPermission4
    ) {
      return true;
    }

    // Vérifier la permission principale de l'item
    if (
      item.routePermission &&
      this.authService.hasPermission(item.routePermission)
    ) {
      return true;
    }

    // Vérifier les permissions enfants
    if (
      item.childPermission1 &&
      this.authService.hasPermission(item.childPermission1)
    ) {
      return true;
    }
    if (
      item.childPermission2 &&
      this.authService.hasPermission(item.childPermission2)
    ) {
      return true;
    }
    if (
      item.childPermission3 &&
      this.authService.hasPermission(item.childPermission3)
    ) {
      return true;
    }
    return !!(
      item.childPermission4 &&
      this.authService.hasPermission(item.childPermission4)
    );
  }

  // Fonction pour vérifier si l'item a au moins un enfant avec permission
  hasAnyChildPermission(item: any): boolean {
    if (!item.children) return false;

    // Vérifier si au moins un enfant a la permission
    return item.children.some((child: any) => {
      if (!child.routePermission) return true; // Afficher si pas de permission requise

      return this.authService.hasPermission(child.routePermission);
    });
  }
}
